import React, { useState } from "react"
import { Link, navigate, useStaticQuery, graphql } from "gatsby"
import styled from "styled-components"
import { media } from "src/styles/breakpoints"
import BaseIcon from "src/components/Base/BaseIcon"
import { Tag, TagInner } from "src/components/Tags"
import { slugify } from "src/utils/helpers"

const ResourcesSearchForm = ({ placeholder = "Search…", className }) => {
  const { allStrapiResource, allStrapiCategory } = useStaticQuery(
    graphql`
      query {
        allStrapiResource {
          nodes {
            Title
            category {
              id
            }
            keywords {
              keyword
            }
            author {
              Name
            }
          }
        }
        allStrapiCategory {
          nodes {
            name
            strapiId
            parent_category {
              name
              id
            }
            resources {
              id
            }
          }
        }
      }
    `
  )

  const [value, setValue] = useState("")
  const [activeListItemIndex, setActiveListItemIndex] = useState(null)

  const resources = allStrapiResource.nodes
  const allCategories = allStrapiCategory.nodes

  const rootPath = "/resources"

  const keywords = getKeywords()
  const authors = getAuthors()

  const matchedResources = value
    ? resources
        .filter((r) => r.Title.toLowerCase().includes(value.toLowerCase()))
        .slice(0, 5)
    : []

  const matchedKeywords = value
    ? keywords
        .filter((k) => k.toLowerCase().includes(value.toLowerCase()))
        // .sort()
        .slice(0, 5)
    : []

  const matchedAuthors =
    value && value.length > 2
      ? authors
          .filter((a) => a.toLowerCase().includes(value.toLowerCase()))
          // .sort()
          .slice(0, 5)
      : []

  const searchregexp = new RegExp(
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    "gi"
  )

  const hasResources = matchedResources.length > 0
  const hasKeywords = matchedKeywords.length > 0
  const hasAuthors = matchedAuthors.length > 0

  function getKeywords() {
    const keywords = []
    resources.forEach((r) => {
      r.keywords.forEach((k) => {
        if (!keywords.includes(k.keyword)) {
          keywords.push(k.keyword)
        }
      })
    })
    return keywords
  }

  function getAuthors() {
    const authors = []
    resources.forEach((r) => {
      const name = (r.author && r.author.Name) || null
      if (name && !authors.includes(name)) {
        authors.push(name)
      }
    })
    return authors
  }

  function getResourcePath(categoryId, title) {
    if (!categoryId) {
      return rootPath
    }

    const categories = []

    function getCat(id) {
      allCategories.forEach((category) => {
        const isCat = category.strapiId === id
        if (!isCat) {
          return
        }
        categories.push(slugify(category.name))
        if (category.parent_category) {
          getCat(category.parent_category.id)
        }
      })
    }

    getCat(categoryId)

    const path = `${rootPath}/${categories
      .reverse()
      .slice(0, 1)
      .join("/")}/${slugify(title)}`

    return path
  }

  function handleAuthorClick(event, author) {
    setValue("")
    navigate(`${rootPath}/authors/${slugify(author)}`)
  }

  function handleSubmit(e) {
    e.preventDefault()
    const query = encodeURIComponent(value)
    let path = `${rootPath}/search/?q=${query}`
    setValue("")
    if (
      typeof activeListItemIndex !== "undefined" &&
      matchedResources.length > 0
    ) {
      const { Title, category } = matchedResources[activeListItemIndex]
      path = getResourcePath(category && category.id, Title)
    }
    navigate(path)
  }

  function handleKeyboard(keyCode, event) {
    if (keyCode === "ArrowUp") {
      setActiveListItemIndex(Math.max(activeListItemIndex - 1, -1))
    } else if (keyCode === "ArrowDown") {
      const index =
        typeof activeListItemIndex === "number" ? activeListItemIndex + 1 : 0
      setActiveListItemIndex(index)
    }
    if (keyCode === "ArrowUp" || keyCode === "ArrowDown") {
      event.preventDefault()
    }
  }

  return (
    <Wrapper
      className={(className || "") + (value ? " -active" : "")}
      onSubmit={handleSubmit}
    >
      <FieldWrapper>
        <Input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(event) => handleKeyboard(event.code, event)}
          aria-label={placeholder}
        />
        <FieldIcon name="search" source="feather" />
      </FieldWrapper>
      {value && (
        <Dropdown>
          {matchedResources.length > 0 && (
            <DropdownGroup>
              <ResourceList>
                {matchedResources.map(({ Title, category }, index) => (
                  <ResourceListItem
                    to={getResourcePath(category && category.id, Title)}
                    className={index === activeListItemIndex && "-active"}
                    key={index}
                    onMouseEnter={() => setActiveListItemIndex(index)}
                    onMouseLeave={() => setActiveListItemIndex(null)}
                  >
                    <ResourceListItemTitle
                      dangerouslySetInnerHTML={{
                        __html: Title.replace(searchregexp, "<em>$&</em>"),
                      }}
                    />
                  </ResourceListItem>
                ))}
              </ResourceList>
            </DropdownGroup>
          )}
          {hasKeywords && (
            <DropdownGroup>
              <DropdownGroupTitle>Keywords</DropdownGroupTitle>
              <KeywordList>
                {matchedKeywords.map((keyword, index) => (
                  <KeywordListItem
                    to={`${rootPath}/keywords/${slugify(keyword)}`}
                    key={index}
                  >
                    <TagInner>{keyword.toLowerCase()}</TagInner>
                  </KeywordListItem>
                ))}
              </KeywordList>
            </DropdownGroup>
          )}
          {hasAuthors && (
            <DropdownGroup>
              <DropdownGroupTitle>Authors</DropdownGroupTitle>
              <ResourceList>
                {matchedAuthors.map((author, index) => (
                  <ResourceListItem
                    as="span"
                    key={index}
                    onClick={(event) => handleAuthorClick(event, author)}
                  >
                    <ResourceListItemTitle
                      dangerouslySetInnerHTML={{
                        __html: author.replace(searchregexp, "<em>$&</em>"),
                      }}
                    />
                  </ResourceListItem>
                ))}
              </ResourceList>
            </DropdownGroup>
          )}
          {value && !hasResources && !hasKeywords && !hasAuthors && (
            <NoResults>No results</NoResults>
          )}
        </Dropdown>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.form`
  width: 490px;
  max-width: 100%;
  position: relative;
  z-index: 2;
  box-shadow: 0 25px 50px rgba(0, 11, 33, 0.07);

  ${media.tablet} {
    &.-active {
      max-width: none;
    }
  }
`

const FieldWrapper = styled.div`
  padding: 5px;
  position: relative;
  z-index: 1;
  background: #fff;
  border-radius: 3px;

  ${Wrapper}.-active & {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`

const Input = styled.input`
  width: 100%;
  padding: 11px 20px 11px 60px;
  border-radius: 3px;
  color: #000;

  &::placeholder {
    color: rgba(116, 115, 125, 0.75);
    transition: 0.15s;
  }

  &:focus {
    &::placeholder {
      color: rgba(116, 115, 125, 0.5);
    }
  }
`

const FieldIcon = styled(BaseIcon)`
  width: 18px;
  height: 18px;
  position: absolute;
  top: 21px;
  left: 20px;
  color: #46cad8;
  pointer-events: none;
  width: 18px;
  height: 18px;
`

const Dropdown = styled.div`
  width: 100%;
  padding: 0 0 10px;
  position: absolute;
  top: 57px;
  left: 0;
  background: #fff;
  border-radius: 0 0 3px 3px;
  box-shadow: 0 25px 50px rgba(0, 11, 33, 0.07);

  ${media.tablet} {
    top: 60px;
  }
`

const DropdownGroup = styled.div`
  &:nth-child(n + 2) {
    margin-top: 20px;
  }
`

const DropdownGroupTitle = styled.h3`
  margin-bottom: 0;
  margin-left: 25px;
  text-transform: uppercase;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.1em;
  color: ${(p) => p.theme.color.textLight};
`

const ResourceList = styled.div`
  font-size: 15px;
  line-height: 1.5;
  color: ${(p) => p.theme.color.textLight};
`

const ResourceListItem = styled(Link)`
  display: block;
  padding: 6px 25px;
  cursor: pointer;

  &.-active,
  &:is(span):hover {
    background: #d5f5f3;
    color: #000;
  }
`

const ResourceListItemTitle = styled.span`
  em {
    font-weight: 500;
    color: #000;
  }
`

const KeywordList = styled.div`
  padding: 13px 20px 0;
  line-height: 1;
`

const KeywordListItem = styled(Tag)``

const NoResults = styled.p`
  margin: 4px 25px;
  font-size: 15px;
  color: #b4b3b8;
`

export default ResourcesSearchForm
